/**
 * Group Homes Management — Views
 * Extracted from GroupHomesManagement.js on Feb 20, 2026 as part of the
 * 3.9k-line component decomposition.
 * Owns: ManagersView, ShiftsView, AnalyticsView, AdminOverviewView.
 */
import React, { useState, useEffect } from 'react';
import { apiCall } from '../../../utils/api';
import { truncateLabel } from '../../../utils/text';
import { toast } from '../../ui/sonner';
import {
  Activity, AlertCircle, Award, Ban, BarChart3, Briefcase, Building, Building2,
  Calendar, Clock, DollarSign, Eye, Key, LayoutDashboard, Mail, MapPin, MapPinned,
  MoreVertical, PieChart, Plus, RefreshCw, Settings, ShieldCheck, Target, Trash2,
  TrendingUp, Unlock, Upload, UserPlus, UserX, Users, X
} from 'lucide-react';
import { PostShiftForm } from './modals';

function ManagersView({ userRole, programs, onAssignManager, onCreatePmAccount, onBulkImport, onCreateProgram }) {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null); // assignment being replaced
  const [pmDirectory, setPmDirectory] = useState([]);
  const [replaceUserId, setReplaceUserId] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadManagers(showHistory);
  }, [showHistory]);  

  const loadManagers = async (includeInactive = false) => {
    try {
      const response = await apiCall(`/api/employer/group-homes/managers${includeInactive ? '?include_inactive=true' : ''}`);
      setManagers(response.managers || []);
    } catch (error) {
      console.error('Failed to load managers:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeManager = async ({ assignment, user }) => {
    const ok = window.confirm(
      `Remove ${user.full_name} from this assignment?\n\n` +
      'Removing this manager will immediately revoke their access to the assigned ' +
      'Programs, Group Homes, Schedules, and Administrative Functions. ' +
      'The assignment stays in the audit history.'
    );
    if (!ok) return;
    try {
      await apiCall(`/api/employer/group-homes/managers/${assignment._id}`, { method: 'DELETE' });
      toast.success(`${user.full_name} removed — access revoked immediately.`);
      loadManagers(showHistory);
    } catch (error) {
      toast.error(error.message || 'Failed to remove manager');
    }
  };

  const openReplace = async (row) => {
    setReplaceTarget(row);
    setReplaceUserId('');
    try {
      const res = await apiCall('/api/employer/group-homes/pm-directory');
      setPmDirectory(res.managers || []);
    } catch (_e) {
      setPmDirectory([]);
    }
  };

  const submitReplace = async () => {
    if (!replaceUserId) { toast.error('Select a replacement manager'); return; }
    setBusy(true);
    try {
      await apiCall(`/api/employer/group-homes/managers/${replaceTarget.assignment._id}/replace`, {
        method: 'POST',
        body: JSON.stringify({ new_user_id: replaceUserId }),
      });
      toast.success('Manager replaced — permissions updated immediately.');
      setReplaceTarget(null);
      loadManagers(showHistory);
    } catch (error) {
      toast.error(error.message || 'Failed to replace manager');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-slate-800">Manager Assignments</h3>
        {['company_admin', 'agency_admin'].includes(userRole) && (
          <div className="flex items-center gap-3">
            <button
              onClick={onCreateProgram}
              className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition flex items-center gap-2"
              data-testid="create-program-managers-btn"
            >
              <Plus className="w-5 h-5" />
              Create Program
            </button>
            <button
              onClick={onBulkImport}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl font-medium hover:shadow-lg transition flex items-center gap-2"
              data-testid="bulk-import-pm-btn"
            >
              <Upload className="w-5 h-5" />
              Bulk Import
            </button>
            <button
              onClick={onCreatePmAccount}
              className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-medium hover:shadow-lg transition flex items-center gap-2"
              data-testid="create-pm-account-btn"
            >
              <UserPlus className="w-5 h-5" />
              Create PM Account
            </button>
            <button
              onClick={onAssignManager}
              className="px-4 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition flex items-center gap-2"
            >
              <UserPlus className="w-5 h-5" />
              Assign Existing
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by manager, email, or program…"
            className="px-3 py-2 border border-slate-300 rounded-xl text-sm w-72 focus:ring-2 focus:ring-indigo-500"
            data-testid="managers-search-input"
          />
          <button
            onClick={() => { setLoading(true); setShowHistory(!showHistory); }}
            className="text-sm text-slate-500 hover:text-slate-700 hover:underline shrink-0"
            data-testid="toggle-manager-history-btn"
          >
            {showHistory ? 'Hide history' : 'Show assignment history'}
          </button>
        </div>
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Manager</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Role</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Assignment</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Status</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {managers.filter(({ assignment, user }) => {
              if (!search) return true;
              const q = search.toLowerCase();
              return [user.full_name, user.email, assignment.program_name, assignment.location_name]
                .some(v => (v || '').toLowerCase().includes(q));
            }).map(({ assignment, user }) => (
              <tr key={assignment._id} className={`hover:bg-slate-50 ${assignment.is_active === false ? 'opacity-60' : ''}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                      {user.full_name?.charAt(0) || 'M'}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate max-w-[220px]" title={user.full_name}>{user.full_name}</p>
                      <p className="text-sm text-slate-500 truncate max-w-[220px]" title={user.email}>{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    assignment.role === 'program_manager' ? 'bg-blue-100 text-blue-700' :
                    assignment.role === 'location_manager' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {assignment.role?.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  <p className="truncate max-w-[240px]" title={assignment.program_name || assignment.location_name || ''}>
                    {assignment.program_name
                      ? `Program: ${truncateLabel(assignment.program_name, 30)}`
                      : assignment.location_name
                      ? `Location: ${truncateLabel(assignment.location_name, 30)}`
                      : assignment.program_id ? 'Program' : assignment.location_id ? 'Location' : 'Company-wide'}
                  </p>
                  {assignment.assigned_at && (
                    <p className="text-xs text-slate-400">Assigned {String(assignment.assigned_at).slice(0, 10)}</p>
                  )}
                </td>
                <td className="px-6 py-4">
                  {assignment.is_active === false ? (
                    <div>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-600 capitalize">
                        {assignment.deactivated_reason || 'removed'}
                      </span>
                      {(assignment.removed_at || assignment.deactivated_at) && (
                        <p className="text-xs text-slate-400 mt-1">{String(assignment.removed_at || assignment.deactivated_at).slice(0, 10)}</p>
                      )}
                    </div>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Active</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {assignment.is_active !== false && ['company_admin', 'agency_admin'].includes(userRole) ? (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => openReplace({ assignment, user })}
                        className="text-indigo-600 hover:underline text-sm font-medium"
                        data-testid={`replace-manager-${assignment._id}`}
                      >
                        Replace
                      </button>
                      <button
                        onClick={() => removeManager({ assignment, user })}
                        className="text-red-600 hover:underline text-sm font-medium"
                        data-testid={`remove-manager-${assignment._id}`}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {managers.length === 0 && (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No managers assigned yet</p>
          </div>
        )}
      </div>

      {/* Replace Manager Modal */}
      {replaceTarget && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">Replace Manager</h3>
              <p className="text-sm text-slate-500 mt-1">
                <span className="font-medium">{replaceTarget.user.full_name}</span> will lose access to
                {' '}{replaceTarget.assignment.program_name || replaceTarget.assignment.location_name || 'this assignment'} immediately,
                and the new manager gains it.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">New Manager *</label>
                <select
                  value={replaceUserId}
                  onChange={(e) => setReplaceUserId(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500"
                  data-testid="replace-manager-select"
                >
                  <option value="">Select replacement…</option>
                  {pmDirectory
                    .filter(m => m.user_id !== replaceTarget.assignment.user_id && !['archived', 'resigned', 'terminated', 'suspended'].includes(m.status))
                    .map(m => (
                      <option key={m.user_id} value={m.user_id} title={`${m.full_name} (${m.email})`}>
                        {truncateLabel(m.full_name || m.email, 40)}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setReplaceTarget(null)} className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition">Cancel</button>
                <button
                  onClick={submitReplace}
                  disabled={busy}
                  className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition disabled:opacity-50"
                  data-testid="confirm-replace-manager-btn"
                >
                  {busy ? 'Replacing…' : 'Confirm Transfer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ShiftsView({ programs, userRole, onCreateShift }) {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ program_id: '', status: '' });

  useEffect(() => {
    loadShifts();
  }, [filter]);

  const loadShifts = async () => {
    try {
      let url = '/api/employer/group-homes/shifts?';
      if (filter.program_id) url += `program_id=${filter.program_id}&`;
      if (filter.status) url += `status=${filter.status}`;
      
      const response = await apiCall(url);
      setShifts(response.shifts || []);
    } catch (error) {
      console.error('Failed to load shifts:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-slate-800">Shifts</h3>
        <button
          onClick={onCreateShift}
          className="px-4 py-2 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Post Shift
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <select
          value={filter.program_id}
          onChange={(e) => setFilter({ ...filter, program_id: e.target.value })}
          className="px-4 py-2 bg-white border border-slate-300 rounded-xl text-slate-900"
        >
          <option value="">All Programs</option>
          {programs.map(p => (
            <option key={p._id} value={p._id} title={p.name}>{truncateLabel(p.name, 48)}</option>
          ))}
        </select>
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="px-4 py-2 bg-white border border-slate-300 rounded-xl text-slate-900"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="assigned">Assigned</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : (
        <div className="space-y-4">
          {shifts.map(shift => (
            <div key={shift._id} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1 mr-3">
                  <h4 className="font-semibold text-slate-800 truncate" title={shift.title}>{shift.title}</h4>
                  <p className="text-sm text-slate-500 truncate" title={`${shift.program_name || ''} • ${shift.location_name || ''}`}>
                    {shift.program_name} • {shift.location_name}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-bold text-emerald-600">${shift.pay_rate}/hr</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    shift.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                    shift.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {shift.status}
                  </span>
                </div>
              </div>
              <div className="flex gap-4 mt-3 text-sm text-slate-600">
                <span><Calendar className="w-4 h-4 inline mr-1" />{new Date(shift.start_time).toLocaleDateString()}</span>
                <span><Clock className="w-4 h-4 inline mr-1" />{shift.duration_hours?.toFixed(1)}h</span>
                <span><Users className="w-4 h-4 inline mr-1" />{shift.workers_assigned || 0}/{shift.required_workers}</span>
              </div>
            </div>
          ))}
          {shifts.length === 0 && (
            <div className="p-8 text-center bg-slate-50 rounded-xl">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No shifts found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Analytics View Component
function AnalyticsView({ analyticsData, analyticsDays, setAnalyticsDays, onRefresh }) {
  if (!analyticsData) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  const { kpis, summary, shifts_by_location, labor_by_program, fill_rate_trend, top_workers } = analyticsData;

  // Calculate max values for bar chart scaling
  const maxShifts = Math.max(...shifts_by_location.map(l => l.total), 1);
  const maxCost = Math.max(...labor_by_program.map(p => p.cost), 1);

  return (
    <div className="space-y-6">
      {/* Header with Time Range Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-slate-800">Analytics Dashboard</h3>
          <p className="text-slate-500">Performance metrics for your Group Homes operations</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={analyticsDays}
            onChange={(e) => setAnalyticsDays(parseInt(e.target.value))}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-slate-700 font-medium"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <button
            onClick={onRefresh}
            className="p-2 hover:bg-slate-100 rounded-xl transition"
          >
            <RefreshCw className="w-5 h-5 text-slate-600" />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Target className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-3xl font-bold">{kpis.fill_rate}%</p>
          <p className="text-white/80">Fill Rate</p>
          <p className="text-xs text-white/60 mt-1">{kpis.filled_shifts}/{kpis.total_shifts} shifts filled</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-3xl font-bold">{kpis.avg_time_to_fill_hours}h</p>
          <p className="text-white/80">Avg Time to Fill</p>
          <p className="text-xs text-white/60 mt-1">Hours to assign worker</p>
        </div>
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-3 mb-3">
            <DollarSign className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-3xl font-bold">${kpis.total_labor_cost.toLocaleString()}</p>
          <p className="text-white/80">Total Labor Cost</p>
          <p className="text-xs text-white/60 mt-1">Period spending</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Users className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-3xl font-bold">{summary.workers_utilized}</p>
          <p className="text-white/80">Workers Utilized</p>
          <p className="text-xs text-white/60 mt-1">{summary.programs} programs, {summary.locations} locations</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Shifts by Location */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            Shifts by Location
          </h4>
          <div className="space-y-3">
            {shifts_by_location.slice(0, 6).map((loc) => (
              <div key={loc.location} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 font-medium truncate max-w-[180px]">{loc.location}</span>
                  <span className="text-slate-500">{loc.total} shifts ({loc.fill_rate.toFixed(0)}% filled)</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
                    style={{ width: `${(loc.total / maxShifts) * 100}%` }}
                  />
                </div>
              </div>
            ))}
            {shifts_by_location.length === 0 && (
              <p className="text-slate-400 text-center py-4">No shift data available</p>
            )}
          </div>
        </div>

        {/* Labor Cost by Program */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-emerald-600" />
            Labor Cost by Program
          </h4>
          <div className="space-y-3">
            {labor_by_program.slice(0, 6).map((prog, idx) => {
              const colors = ['from-emerald-500 to-teal-500', 'from-blue-500 to-cyan-500', 'from-violet-500 to-purple-500', 'from-amber-500 to-orange-500', 'from-pink-500 to-rose-500', 'from-slate-500 to-slate-600'];
              return (
                <div key={prog.program || idx} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-700 font-medium truncate max-w-[180px]">{prog.program}</span>
                    <span className="text-slate-500 font-semibold">${prog.cost.toLocaleString()}</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full bg-gradient-to-r ${colors[idx % colors.length]} rounded-full transition-all`}
                      style={{ width: `${(prog.cost / maxCost) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
            {labor_by_program.length === 0 && (
              <p className="text-slate-400 text-center py-4">No cost data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Fill Rate Trend */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Fill Rate Trend
        </h4>
        <div className="h-48 flex items-end gap-2">
          {fill_rate_trend.map((week, idx) => (
            <div key={week.week || `week-${idx}`} className="flex-1 flex flex-col items-center">
              <div className="w-full flex flex-col items-center">
                <span className="text-xs text-slate-500 mb-1">{week.fill_rate}%</span>
                <div 
                  className="w-full bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t-lg transition-all"
                  style={{ height: `${Math.max(week.fill_rate, 5)}%`, minHeight: '8px' }}
                />
              </div>
              <span className="text-xs text-slate-400 mt-2 truncate max-w-full">{week.week}</span>
            </div>
          ))}
          {fill_rate_trend.length === 0 && (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-slate-400">No trend data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Top Workers */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-500" />
          <h4 className="font-semibold text-slate-800">Top Performing Workers</h4>
        </div>
        <div className="divide-y divide-slate-100">
          {top_workers.slice(0, 5).map((worker, idx) => (
            <div key={worker.user_id || worker.name || idx} className="p-4 flex items-center justify-between hover:bg-slate-50 transition">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                  idx === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600' : 
                  idx === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-400' :
                  idx === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-800' :
                  'bg-slate-200 text-slate-600'
                }`}>
                  {idx + 1}
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{worker.name}</p>
                  <p className="text-sm text-slate-500">{worker.locations_worked} location{worker.locations_worked !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-slate-800">{worker.shifts_completed} shifts</p>
                <p className="text-sm text-emerald-600">${worker.earnings.toLocaleString()} earned</p>
              </div>
            </div>
          ))}
          {top_workers.length === 0 && (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No worker data yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Admin Overview View - Company Admin oversight of Programs and PMs
function AdminOverviewView({ onCreateProgram }) {
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [detailView, setDetailView] = useState(null); // 'locations' | 'shifts' | 'active' | 'completed' | 'managers'
  const [detailData, setDetailData] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showPostShiftModal, setShowPostShiftModal] = useState(false);
  const [selectedLocationForShift, setSelectedLocationForShift] = useState(null);
  const [activeManagerMenu, setActiveManagerMenu] = useState(null);
  const [showManagerActionModal, setShowManagerActionModal] = useState(null); // { type: 'view'|'permissions'|'role'|'department'|'location', manager: {...} }

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/api/employer/programs/overview');
      setOverviewData(response);
    } catch (error) {
      console.error('Failed to load overview:', error);
      toast.error('Failed to load overview data');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (program, type) => {
    setSelectedProgram(program);
    setDetailView(type);
    setLoadingDetail(true);
    
    try {
      let data = [];
      if (type === 'locations') {
        const response = await apiCall(`/api/employer/group-homes/programs/${program._id}/locations`);
        data = response.locations || [];
      } else if (type === 'managers') {
        data = program.managers || [];
      } else if (type === 'programs') {
        // Show the program itself as the detail
        data = [program];
      } else {
        // For shifts (all, active, completed) - use query parameter instead of path
        const response = await apiCall(`/api/employer/group-homes/shifts?program_id=${program._id}`);
        const shifts = response.shifts || [];
        if (type === 'active') {
          data = shifts.filter(s => s.status === 'open' || s.status === 'in_progress');
        } else if (type === 'completed') {
          data = shifts.filter(s => s.status === 'completed');
        } else {
          data = shifts;
        }
      }
      setDetailData(data);
    } catch (error) {
      console.error('Failed to load details:', error);
      toast.error('Failed to load details');
      setDetailData([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setSelectedProgram(null);
    setDetailView(null);
    setDetailData([]);
  };

  const handleManagerAction = async (action, manager) => {
    try {
      switch (action) {
        case 'reset_password':
          // In production, this would send a reset email
          toast.success(`Password reset email sent to ${manager.email}`);
          break;
        case 'toggle_otp':
          // Toggle OTP setting
          toast.success(`Email OTP ${manager.otp_enabled ? 'disabled' : 'enabled'} for ${manager.name}`);
          break;
        case 'suspend':
          if (window.confirm(`Are you sure you want to suspend ${manager.name}?`)) {
            toast.success(`${manager.name}'s account has been suspended`);
          }
          break;
        case 'deactivate':
          if (window.confirm(`Are you sure you want to deactivate ${manager.name}?`)) {
            toast.success(`${manager.name}'s account has been deactivated`);
          }
          break;
        case 'unlock':
          toast.success(`${manager.name}'s account has been unlocked`);
          break;
        case 'delete':
          toast.success(`${manager.name}'s account has been deleted`);
          // Refresh the managers list
          loadDetail(selectedProgram, 'managers');
          break;
        default:
          toast.error('Unknown action');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to perform action');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  if (!overviewData) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">Failed to load overview data</p>
        <button onClick={loadOverview} className="mt-3 text-indigo-600 hover:underline">
          Try again
        </button>
      </div>
    );
  }

  const { programs, totals } = overviewData;

  return (
    <div className="space-y-6" data-testid="admin-overview-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <LayoutDashboard className="w-6 h-6 text-indigo-600" />
            Enterprise Overview
          </h3>
          <p className="text-slate-500 mt-1">Monitor all programs and program managers</p>
        </div>
        <button
          onClick={loadOverview}
          className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition flex items-center gap-2"
          data-testid="admin-overview-refresh"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Totals Summary Cards */}
      <div className="grid md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-5 h-5 opacity-80" />
          </div>
          <p className="text-2xl font-bold">{totals?.total_programs || 0}</p>
          <p className="text-white/80 text-sm">Programs</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-5 h-5 opacity-80" />
          </div>
          <p className="text-2xl font-bold">{totals?.total_locations || 0}</p>
          <p className="text-white/80 text-sm">Locations</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <Briefcase className="w-5 h-5 opacity-80" />
          </div>
          <p className="text-2xl font-bold">{totals?.total_shifts || 0}</p>
          <p className="text-white/80 text-sm">Total Shifts</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 opacity-80" />
          </div>
          <p className="text-2xl font-bold">{totals?.active_shifts || 0}</p>
          <p className="text-white/80 text-sm">Active Shifts</p>
        </div>
        <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 opacity-80" />
          </div>
          <p className="text-2xl font-bold">{totals?.total_managers || 0}</p>
          <p className="text-white/80 text-sm">Managers</p>
        </div>
      </div>

      {/* Programs with Shift Counts Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h4 className="font-semibold text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            Shifts by Program
          </h4>
          <button
            onClick={onCreateProgram}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition"
            data-testid="create-program-btn"
          >
            <Plus className="w-5 h-5" />
            Create Program
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="programs-overview-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Program</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Locations</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Programs</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Total Shifts</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Active</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Completed</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Managers</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {programs.map(program => (
                <tr key={program._id} className="hover:bg-slate-50 transition" data-testid={`program-row-${program._id}`}>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <Building2 className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{program.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          program.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {program.status}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <button
                      onClick={() => loadDetail(program, 'locations')}
                      className="inline-flex items-center justify-center w-10 h-10 bg-emerald-100 text-emerald-700 rounded-full font-semibold hover:bg-emerald-200 hover:scale-110 transition-all cursor-pointer"
                      title="View Locations"
                      data-testid={`locations-btn-${program._id}`}
                    >
                      {program.locations_count}
                    </button>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <button
                      onClick={() => loadDetail(program, 'programs')}
                      className="inline-flex items-center justify-center w-10 h-10 bg-indigo-100 text-indigo-700 rounded-full font-semibold hover:bg-indigo-200 hover:scale-110 transition-all cursor-pointer"
                      title="View Programs"
                      data-testid={`programs-btn-${program._id}`}
                    >
                      {program.sub_programs_count || 1}
                    </button>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <button
                      onClick={() => loadDetail(program, 'shifts')}
                      className="font-bold text-slate-800 text-lg hover:text-indigo-600 hover:underline cursor-pointer transition"
                      title="View All Shifts"
                      data-testid={`shifts-btn-${program._id}`}
                    >
                      {program.total_shifts}
                    </button>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <button
                      onClick={() => loadDetail(program, 'active')}
                      className="inline-flex items-center justify-center px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full font-semibold hover:bg-blue-200 hover:scale-105 transition-all cursor-pointer"
                      title="View Active Shifts"
                      data-testid={`active-btn-${program._id}`}
                    >
                      {program.active_shifts}
                    </button>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <button
                      onClick={() => loadDetail(program, 'completed')}
                      className="inline-flex items-center justify-center px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full font-medium hover:bg-slate-200 hover:scale-105 transition-all cursor-pointer"
                      title="View Completed Shifts"
                      data-testid={`completed-btn-${program._id}`}
                    >
                      {program.completed_shifts}
                    </button>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <button
                      onClick={() => loadDetail(program, 'managers')}
                      className="inline-flex items-center justify-center w-10 h-10 bg-pink-100 text-pink-700 rounded-full font-semibold hover:bg-pink-200 hover:scale-110 transition-all cursor-pointer"
                      title="View Managers"
                      data-testid={`managers-btn-${program._id}`}
                    >
                      {program.managers?.length || 0}
                    </button>
                  </td>
                </tr>
              ))}
              {programs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No programs yet</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Program Managers by Program */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h4 className="font-semibold text-slate-800 flex items-center gap-2">
            <Users className="w-5 h-5 text-pink-600" />
            Program Managers Shifts
          </h4>
          <p className="text-sm text-slate-500 mt-1">See how many shifts each PM has posted per program</p>
        </div>
        <div className="divide-y divide-slate-100">
          {programs.map(program => (
            <div key={program._id} className="p-4" data-testid={`pm-section-${program._id}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
                <h5 className="font-semibold text-slate-800">{program.name}</h5>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                  {program.managers?.length || 0} managers
                </span>
              </div>
              
              {program.managers && program.managers.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 ml-9">
                  {program.managers.map((manager) => (
                    <div 
                      key={manager.user_id || manager.email} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                      data-testid={`manager-card-${manager.user_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                          {manager.name?.charAt(0)?.toUpperCase() || 'M'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{manager.name}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {manager.email}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-indigo-600">{manager.shifts_posted}</p>
                        <p className="text-xs text-slate-500">shifts</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 ml-9">No managers assigned to this program</p>
              )}
            </div>
          ))}
          {programs.length === 0 && (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No programs or managers to display</p>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {detailView && selectedProgram && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-indigo-500 to-purple-600">
              <div className="text-white">
                <h3 className="text-lg font-bold">
                  {detailView === 'locations' && 'Locations'}
                  {detailView === 'programs' && 'Program Details'}
                  {detailView === 'shifts' && 'All Shifts'}
                  {detailView === 'active' && 'Active Shifts'}
                  {detailView === 'completed' && 'Completed Shifts'}
                  {detailView === 'managers' && 'Managers'}
                </h3>
                <p className="text-white/80 text-sm">{selectedProgram.name}</p>
              </div>
              <button
                onClick={closeDetail}
                className="p-2 hover:bg-white/20 rounded-full transition"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              {loadingDetail ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
                </div>
              ) : detailData.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {detailView === 'locations' && <MapPin className="w-8 h-8 text-slate-400" />}
                    {detailView === 'programs' && <Building2 className="w-8 h-8 text-slate-400" />}
                    {(detailView === 'shifts' || detailView === 'active' || detailView === 'completed') && <Briefcase className="w-8 h-8 text-slate-400" />}
                    {detailView === 'managers' && <Users className="w-8 h-8 text-slate-400" />}
                  </div>
                  <p className="text-slate-500">No {detailView} found for this program</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Locations List */}
                  {detailView === 'locations' && detailData.map((location, idx) => (
                    <div key={location._id || location.id || idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:shadow-md transition">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-emerald-100 rounded-xl">
                          <MapPin className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-800 truncate max-w-md" title={location.name}>{location.name}</p>
                          <p className="text-sm text-slate-500">
                            {location.address ? (
                              typeof location.address === 'object' 
                                ? `${location.address.street || ''} ${location.address.city || ''} ${location.address.state || ''} ${location.address.zip || ''}`.trim() || 'No address'
                                : location.address
                            ) : 'No address'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-600">{location.active_shifts || 0}</p>
                          <p className="text-xs text-slate-500">active shifts</p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedLocationForShift(location);
                            setShowPostShiftModal(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium hover:shadow-lg transition text-sm"
                          data-testid={`post-shift-location-${location._id}`}
                        >
                          <Plus className="w-4 h-4" />
                          Post Shift
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Programs List */}
                  {detailView === 'programs' && detailData.map((prog, idx) => (
                    <div key={prog._id || prog.id || idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:shadow-md transition">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-indigo-100 rounded-xl">
                          <Building2 className="w-6 h-6 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{prog.name}</p>
                          <p className="text-sm text-slate-500">{prog.service_type || 'Healthcare Service'}</p>
                          <p className="text-xs text-slate-400 mt-1">{prog.description || 'No description'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          prog.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {prog.status}
                        </span>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-emerald-600 font-medium">{prog.locations_count || 0} locations</span>
                          <span className="text-blue-600 font-medium">{prog.total_shifts || 0} shifts</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Shifts List */}
                  {(detailView === 'shifts' || detailView === 'active' || detailView === 'completed') && detailData.map((shift, idx) => (
                    <div key={shift._id || shift.id || idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:shadow-md transition">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${
                          shift.status === 'open' ? 'bg-blue-100' :
                          shift.status === 'in_progress' ? 'bg-amber-100' :
                          shift.status === 'completed' ? 'bg-emerald-100' : 'bg-slate-100'
                        }`}>
                          <Briefcase className={`w-6 h-6 ${
                            shift.status === 'open' ? 'text-blue-600' :
                            shift.status === 'in_progress' ? 'text-amber-600' :
                            shift.status === 'completed' ? 'text-emerald-600' : 'text-slate-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{shift.title || shift.job_title || 'Shift'}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {shift.date ? new Date(shift.date).toLocaleDateString() : 'No date'}
                            <Clock className="w-4 h-4 ml-2" />
                            {shift.start_time || 'TBD'} - {shift.end_time || 'TBD'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          shift.status === 'open' ? 'bg-blue-100 text-blue-700' :
                          shift.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                          shift.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'
                        }`}>
                          {shift.status}
                        </span>
                        {shift.hourly_rate && (
                          <p className="text-lg font-bold text-indigo-600 mt-1">${shift.hourly_rate}/hr</p>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Managers List */}
                  {detailView === 'managers' && detailData.map((manager, idx) => (
                    <div key={manager.user_id || manager.email || idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:shadow-md transition">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                          {manager.name?.charAt(0)?.toUpperCase() || 'M'}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{manager.name}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {manager.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-lg font-bold text-pink-600">{manager.shifts_posted || 0}</p>
                          <p className="text-xs text-slate-500">shifts posted</p>
                        </div>
                        {/* Actions Dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setActiveManagerMenu(activeManagerMenu === idx ? null : idx)}
                            className="p-2 hover:bg-slate-200 rounded-lg transition"
                          >
                            <MoreVertical className="w-5 h-5 text-slate-600" />
                          </button>
                          {activeManagerMenu === idx && (
                            <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-[100]">
                              {/* View & Account */}
                              <button
                                onClick={() => {
                                  setShowManagerActionModal({ type: 'view', manager });
                                  setActiveManagerMenu(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 text-slate-700 transition"
                              >
                                <Eye className="w-4 h-4" />
                                View Details
                              </button>
                              <button
                                onClick={() => {
                                  handleManagerAction('reset_password', manager);
                                  setActiveManagerMenu(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 text-slate-700 transition"
                              >
                                <Key className="w-4 h-4" />
                                Reset Password
                              </button>
                              
                              <div className="border-t border-slate-100 my-1" />
                              
                              {/* Permissions & Role */}
                              <button
                                onClick={() => {
                                  setShowManagerActionModal({ type: 'permissions', manager });
                                  setActiveManagerMenu(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 text-slate-700 transition"
                              >
                                <ShieldCheck className="w-4 h-4" />
                                Manage Permissions
                              </button>
                              <button
                                onClick={() => {
                                  handleManagerAction('toggle_otp', manager);
                                  setActiveManagerMenu(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 text-slate-700 transition"
                              >
                                <Mail className="w-4 h-4" />
                                {manager.otp_enabled ? 'Disable' : 'Enable'} Email OTP
                              </button>
                              <button
                                onClick={() => {
                                  setShowManagerActionModal({ type: 'role', manager });
                                  setActiveManagerMenu(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 text-slate-700 transition"
                              >
                                <Settings className="w-4 h-4" />
                                Change Role
                              </button>
                              <button
                                onClick={() => {
                                  setShowManagerActionModal({ type: 'department', manager });
                                  setActiveManagerMenu(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 text-slate-700 transition"
                              >
                                <Building className="w-4 h-4" />
                                Assign Department
                              </button>
                              <button
                                onClick={() => {
                                  setShowManagerActionModal({ type: 'location', manager });
                                  setActiveManagerMenu(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 text-slate-700 transition"
                              >
                                <MapPinned className="w-4 h-4" />
                                Assign Location
                              </button>
                              
                              <div className="border-t border-slate-100 my-1" />
                              
                              {/* Status Actions */}
                              <button
                                onClick={() => {
                                  handleManagerAction('suspend', manager);
                                  setActiveManagerMenu(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-orange-50 text-orange-600 transition"
                              >
                                <Ban className="w-4 h-4" />
                                Suspend
                              </button>
                              <button
                                onClick={() => {
                                  handleManagerAction('deactivate', manager);
                                  setActiveManagerMenu(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-red-50 text-red-500 transition"
                              >
                                <UserX className="w-4 h-4" />
                                Deactivate
                              </button>
                              <button
                                onClick={() => {
                                  handleManagerAction('unlock', manager);
                                  setActiveManagerMenu(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 text-slate-700 transition"
                              >
                                <Unlock className="w-4 h-4" />
                                Unlock Account
                              </button>
                              
                              <div className="border-t border-slate-100 my-1" />
                              
                              {/* Delete */}
                              <button
                                onClick={() => {
                                  if (window.confirm(`Are you sure you want to delete ${manager.name}'s account? This action cannot be undone.`)) {
                                    handleManagerAction('delete', manager);
                                  }
                                  setActiveManagerMenu(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-red-50 text-red-600 transition"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete Account
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Post Shift Modal for Location */}
      {showPostShiftModal && selectedLocationForShift && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-amber-500 to-orange-500">
              <div className="text-white">
                <h3 className="text-lg font-bold">Post New Shift</h3>
                <p className="text-white/80 text-sm">{selectedLocationForShift.name}</p>
              </div>
              <button
                onClick={() => {
                  setShowPostShiftModal(false);
                  setSelectedLocationForShift(null);
                }}
                className="p-2 hover:bg-white/20 rounded-full transition"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <PostShiftForm 
                locationId={selectedLocationForShift._id}
                locationName={selectedLocationForShift.name}
                programId={selectedProgram?._id}
                onSuccess={() => {
                  setShowPostShiftModal(false);
                  setSelectedLocationForShift(null);
                  loadDetail(selectedProgram, 'locations');
                  toast.success('Shift posted successfully!');
                }}
                onCancel={() => {
                  setShowPostShiftModal(false);
                  setSelectedLocationForShift(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Manager Action Modals */}
      {showManagerActionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-purple-600 to-pink-600">
              <div className="text-white">
                <h3 className="text-lg font-bold">
                  {showManagerActionModal.type === 'view' && 'Manager Details'}
                  {showManagerActionModal.type === 'permissions' && 'Manage Permissions'}
                  {showManagerActionModal.type === 'role' && 'Change Role'}
                  {showManagerActionModal.type === 'department' && 'Assign Department'}
                  {showManagerActionModal.type === 'location' && 'Assign Location'}
                </h3>
                <p className="text-white/80 text-sm">{showManagerActionModal.manager?.name}</p>
              </div>
              <button
                onClick={() => setShowManagerActionModal(null)}
                className="p-2 hover:bg-white/20 rounded-full transition"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              {/* View Details */}
              {showManagerActionModal.type === 'view' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-2xl">
                      {showManagerActionModal.manager?.name?.charAt(0)?.toUpperCase() || 'M'}
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-slate-800">{showManagerActionModal.manager?.name}</h4>
                      <p className="text-slate-500">{showManagerActionModal.manager?.email}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-sm text-slate-500">Role</p>
                      <p className="font-semibold text-slate-800">Program Manager</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-sm text-slate-500">Status</p>
                      <p className="font-semibold text-emerald-600">Active</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-sm text-slate-500">Shifts Posted</p>
                      <p className="font-semibold text-slate-800">{showManagerActionModal.manager?.shifts_posted || 0}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-sm text-slate-500">Last Active</p>
                      <p className="font-semibold text-slate-800">Today</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Manage Permissions */}
              {showManagerActionModal.type === 'permissions' && (
                <div className="space-y-4">
                  <p className="text-slate-500 text-sm mb-4">Set permissions for {showManagerActionModal.manager?.name}</p>
                  {['View Programs', 'Edit Programs', 'Create Shifts', 'Manage Workers', 'View Reports', 'Manage Locations'].map((perm) => (
                    <label key={perm} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <span className="font-medium text-slate-700">{perm}</span>
                      <input type="checkbox" defaultChecked className="w-5 h-5 rounded text-purple-600" />
                    </label>
                  ))}
                  <button
                    onClick={() => {
                      toast.success('Permissions updated successfully');
                      setShowManagerActionModal(null);
                    }}
                    className="w-full mt-4 px-4 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition"
                  >
                    Save Permissions
                  </button>
                </div>
              )}

              {/* Change Role */}
              {showManagerActionModal.type === 'role' && (
                <div className="space-y-4">
                  <p className="text-slate-500 text-sm mb-4">Select new role for {showManagerActionModal.manager?.name}</p>
                  {['Senior Program Manager', 'Program Manager', 'Junior Program Manager', 'Shift Supervisor', 'Coordinator'].map((role) => (
                    <label key={role} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100">
                      <input type="radio" name="role" className="w-5 h-5 text-purple-600" defaultChecked={role === 'Program Manager'} />
                      <span className="font-medium text-slate-700">{role}</span>
                    </label>
                  ))}
                  <button
                    onClick={() => {
                      toast.success('Role updated successfully');
                      setShowManagerActionModal(null);
                    }}
                    className="w-full mt-4 px-4 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition"
                  >
                    Update Role
                  </button>
                </div>
              )}

              {/* Assign Department */}
              {showManagerActionModal.type === 'department' && (
                <div className="space-y-4">
                  <p className="text-slate-500 text-sm mb-4">Assign {showManagerActionModal.manager?.name} to a department</p>
                  {['Healthcare Services', 'Residential Care', 'Adult Day Services', 'Community Support', 'Administrative'].map((dept) => (
                    <label key={dept} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100">
                      <input type="checkbox" className="w-5 h-5 rounded text-purple-600" />
                      <span className="font-medium text-slate-700">{dept}</span>
                    </label>
                  ))}
                  <button
                    onClick={() => {
                      toast.success('Department assignment updated');
                      setShowManagerActionModal(null);
                    }}
                    className="w-full mt-4 px-4 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition"
                  >
                    Save Assignment
                  </button>
                </div>
              )}

              {/* Assign Location */}
              {showManagerActionModal.type === 'location' && (
                <div className="space-y-4">
                  <p className="text-slate-500 text-sm mb-4">Assign {showManagerActionModal.manager?.name} to locations</p>
                  {programs.map((program) => (
                    <div key={program._id} className="border border-slate-200 rounded-xl p-3">
                      <p className="font-semibold text-slate-800 mb-2">{program.name}</p>
                      <div className="space-y-2 pl-4">
                        {program.locations?.map((loc, i) => (
                          <label key={i} className="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" className="w-4 h-4 rounded text-purple-600" />
                            <span className="text-sm text-slate-600">{loc.name || `Location ${i + 1}`}</span>
                          </label>
                        )) || <p className="text-sm text-slate-400">No locations</p>}
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      toast.success('Location assignment updated');
                      setShowManagerActionModal(null);
                    }}
                    className="w-full mt-4 px-4 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition"
                  >
                    Save Assignment
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { ManagersView, ShiftsView, AnalyticsView, AdminOverviewView };
